import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { PostHogProvider } from 'posthog-js/react'
import { initSentry, SentryErrorBoundary } from './lib/sentry'
import { initBuildInfo, setBuildContextForSentry } from './lib/build-info'
import './index.css'
import './styles/accessibility.css'
import App from './App.tsx'

// Initialize build information (synchronous — cheap)
initBuildInfo();

// Defer Sentry init out of the JS critical path so it doesn't block FCP.
// requestIdleCallback fires after the first paint; setTimeout(0) fallback for
// browsers that don't support it (e.g. Safari < 16.4).
const deferSentryInit = () => {
  initSentry();
  setBuildContextForSentry();
};
if ('requestIdleCallback' in window) {
  requestIdleCallback(deferSentryInit);
} else {
  setTimeout(deferSentryInit, 0);
}

// PostHog configuration options
const posthogOptions = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com',
  // Capture page views automatically
  capture_pageview: true,
  // Capture page leaves for session duration
  capture_pageleave: true,
  // Enable autocapture for clicks, inputs, etc.
  autocapture: true,
  // Persist across sessions
  persistence: 'localStorage+cookie' as const,
  // Session recording (disabled by default for privacy)
  disable_session_recording: true,
  // Respect Do Not Track browser setting
  respect_dnt: true,
  // Mask text in session recordings for privacy
  mask_all_text: true,
  mask_all_element_attributes: true,
  // EU-compliant (no IP tracking)
  ip: false,
}

const posthogApiKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY || ''

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <SentryErrorBoundary
      fallback={({ error, resetError }) => (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
            <h1 className="text-xl font-semibold text-red-600 mb-4">Something went wrong</h1>
            <p className="text-gray-600 mb-4">
              We've been notified about this error and will fix it soon.
            </p>
            <button
              onClick={resetError}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
            >
              Try again
            </button>
            <details className="mt-4">
              <summary className="cursor-pointer text-sm text-gray-500">Error details</summary>
              <pre className="mt-2 text-xs text-gray-400 overflow-auto">{error instanceof Error ? error.message : String(error)}</pre>
            </details>
          </div>
        </div>
      )}
    >
      {posthogApiKey ? (
        <PostHogProvider apiKey={posthogApiKey} options={posthogOptions}>
          <App />
        </PostHogProvider>
      ) : (
        <App />
      )}
    </SentryErrorBoundary>
  </StrictMode>,
)